/**
 * The `t()` seam. Every user-facing string in the client goes through it — no literal copy in
 * components — so a second locale is a data change, not a code change.
 */
import { en, type StringKey } from './en.js';

const LOCALES = { en } as const;

export type Locale = keyof typeof LOCALES;
export const DEFAULT_LOCALE: Locale = 'en';

let active: Locale = DEFAULT_LOCALE;

export function setLocale(locale: Locale): void {
  active = locale in LOCALES ? locale : DEFAULT_LOCALE;
  document.documentElement.lang = active;
}

export function locale(): Locale {
  return active;
}

/**
 * Look up a string, optionally interpolating `{name}` placeholders.
 * Missing keys fall back to the default locale, then to the key itself (visible in dev, harmless
 * in production).
 */
export function t(key: StringKey, vars?: Record<string, string | number>): string {
  const table = LOCALES[active] as Record<string, string>;
  const fallback = LOCALES[DEFAULT_LOCALE] as Record<string, string>;
  const template = table[key] ?? fallback[key] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export type { StringKey };
